import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Award, BarChart3, ChevronLeft, ChevronRight, Flame, Home, Gamepad2,
  LockKeyhole, LogOut, Mail, Menu, MessageSquare, Settings, Shield,
  Target, Trophy, Users, X, CreditCard, Webcam,
} from 'lucide-react';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { Facebook, Instagram } from 'lucide-react';
import { OPEN_FEEDBACK_EVENT } from '@/components/FeedbackWidget';
// Logo aus der unveränderten originalen Claude-ZIP, Git-Blob SHA 75c5fc19...
const logo = '/liga-design/logo.webp';
import AbmeldenDialog from '@/components/redesign/AbmeldenDialog';
import '@/styles/aimup-redesign-navigation.css';

type NavItem = {
  label: string;
  path: string;
  /** Weitere Seiten, auf denen der Eintrag als aktiv gilt (Vorlage: Performance-Test gehört zu „Persönliches Training“). */
  also?: string[];
  Icon: React.ElementType;
  locked?: 'challenge';
  badge?: number;
};

const LOCK_MESSAGES = {
  challenge: 'Die Wochenchallenge pausiert. Zurzeit sind keine Spiele verfügbar.',
} as const;
/** Signal für den Wochenchallenge-Hinweis (Navigation, Dashboard-Karte). */
export const CHALLENGE_HINT_EVENT = 'aimup-challenge-hint';
// Hinweis-Text kurz genug für eine Zeile auf dem Handy (Vorlage: 1 Zeile, 2,2 s).
const CHALLENGE_HINT = 'Die Wochenchallenge pausiert zurzeit.';

/**
 * Neue Navigationsoberfläche aus dem freigegebenen Claude-Design.
 * Gemeinsame Navigation für authentifizierte App-Seiten; keine Spiellogik und keine Backend-Änderungen.
 */
export default function AimUpNavigation() {
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { isAdmin } = useUserRole();
  const { profile } = useProfile();
  const { signOut } = useAuth();
  const unread = useUnreadMessages();
  const de = language === 'de';
  const nickname = profile?.display_name || profile?.username || '';
  const initials = nickname.trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase() || 'AU';

  const [tabletExpanded, setTabletExpanded] = useState(false);
  const [mobileMore, setMobileMore] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [askSignOut, setAskSignOut] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    const show = () => setHint(CHALLENGE_HINT);
    window.addEventListener(CHALLENGE_HINT_EVENT, show);
    return () => window.removeEventListener(CHALLENGE_HINT_EVENT, show);
  }, []);
  useEffect(() => {
    if (!hint) return;
    const t = window.setTimeout(() => setHint(null), 2200);
    return () => window.clearTimeout(t);
  }, [hint]);
  const current = location.pathname.replace(/^\/(de|en)(?=\/|$)/, '') || '/';

  const sections: Array<{ title?: string; entries: NavItem[] }> = [
    {
      entries: [{ label: 'Dashboard', path: '/startseite', Icon: Home }],
    },
    {
      title: 'Trainieren',
      entries: [
        { label: 'Persönliches Training', path: '/meine-trainings', Icon: Target, also: ['/analyse'] },
        { label: 'AimUp Liga', path: '/liga', Icon: Trophy },
      ],
    },
    { title: 'Challenge', entries: [{ label: 'Wochenchallenge', path: '/wochenchallenge', Icon: Flame, locked: 'challenge' }] },
    { title: 'Spielen', entries: [{ label: 'Lokale Spiele', path: '/lokale-spiele', Icon: Gamepad2 }] },
    {
      title: 'Fortschritt',
      entries: [
        { label: 'Statistiken', path: '/statistics', Icon: BarChart3 },
        { label: 'Auszeichnungen', path: '/auszeichnungen', Icon: Award },
      ],
    },
    {
      title: 'Community',
      entries: [
        { label: 'Freunde', path: '/friends', Icon: Users },
        { label: 'Nachrichten', path: '/inbox', Icon: Mail, badge: unread },
      ],
    },
    // Letzte reguläre Kategorie: Downloads (Board Manager, Bezug über den Microsoft Store).
    { title: 'Downloads', entries: [{ label: 'Board Manager', path: '/board-manager', Icon: Webcam }] },
  ];
  // Menü „Mehr“ (Vorlage layout_appNavigation_mobile_menueMehr): Abschnitt „Weitere“.
  const more: NavItem[] = [
    { label: 'Einstellungen', path: '/profile', Icon: Settings },
    // Nicht in der Vorlage; bleibt, damit das Abo wie bisher direkt erreichbar ist (Entscheidung Betreiber offen).
    { label: 'Mein Abo', path: '/profile?tab=subscription', Icon: CreditCard },
    ...(isAdmin ? [{ label: 'Admin-Bereich', path: '/admin', Icon: Shield }] : []),
  ];
  const go = (item: NavItem) => {
    if (item.locked) {
      setNotice(null);
      setMobileMore(false);
      setHint(CHALLENGE_HINT);
      return;
    }
    setNotice(null);
    setMobileMore(false);
    setTabletExpanded(false);
    navigate(item.path);
  };

  // Nur der Reiter-Parameter unterscheidet Einträge; andere Parameter (z. B. Rückkehr aus dem Checkout) nicht.
  const isActive = (itemPath: string, also: string[] = []) => {
    if (also.includes(current)) return true;
    const [path, search] = itemPath.split('?');
    return current === path && (search ? location.search === '?' + search : !new URLSearchParams(location.search).get('tab'));
  };

  const renderItem = (item: NavItem, mode: 'side' | 'mobile') => {
    const active = isActive(item.path, item.also);
    return (
      <button
        key={item.label}
        type="button"
        className={`aur-navitem ${active ? 'aur-navitem-active' : ''} ${item.locked ? 'aur-navitem-locked' : ''}`}
        title={item.locked ? LOCK_MESSAGES[item.locked] : item.label}
        aria-label={item.locked ? `${item.label} – gesperrt` : item.label}
        aria-disabled={item.locked ? true : undefined}
        onClick={() => go(item)}
      >
        <item.Icon className="aur-icon" aria-hidden="true" />
        <span className="aur-navlabel">{item.label}</span>
        {!item.locked && (item.badge ?? 0) > 0 && (
          <span className="aur-count">{Math.min(item.badge!, 99)}</span>
        )}
      </button>
    );
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
      setNotice(null);
      setMobileMore(false);
      setTabletExpanded(false);
      navigate('/', { replace: true });
    } catch {
      setNotice('Abmelden fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setSigningOut(false);
    }
  };

  /** P13 (9.A-1): Seitenleiste und Menü „Mehr“ fragen vor dem Abmelden nach (gleiche Rückfrage wie Einstellungen). */
  const askForSignOut = () => {
    setMobileMore(false);
    setAskSignOut(true);
  };

  const openFeedback = () => {
    setNotice(null);
    setMobileMore(false);
    window.dispatchEvent(new Event(OPEN_FEEDBACK_EVENT));
  };

  const desktopAndTabletNav = (
    <>
      <button className="aur-logo" type="button" onClick={() => navigate('/startseite')} aria-label="Zum Dashboard">
        <img src={logo} alt="AimUp" />
      </button>
      <div className="aur-sections">
        {sections.map((section, i) => (
          <div key={section.title || i} className="aur-section">
            {section.title && <div className="aur-section-title">{section.title}</div>}
            {section.entries.map(entry => renderItem(entry, 'side'))}
          </div>
        ))}
      </div>
      <div className="aur-side-bottom">
        <div className="aur-socials" aria-label="AimUp Social Media">
          <span title="Facebook" aria-label="Facebook"><Facebook size={16} /></span>
          <span title="Instagram" aria-label="Instagram"><Instagram size={16} /></span>
        </div>
        {isAdmin && (
          <button className="aur-side-admin" type="button" onClick={() => navigate('/admin')}>
            <Shield className="aur-icon" aria-hidden="true" />
            <span className="aur-navlabel">Admin-Bereich</span>
          </button>
        )}
        <button type="button" onClick={openFeedback}>
          <MessageSquare className="aur-icon" aria-hidden="true" />
          <span className="aur-navlabel">Feedback</span>
        </button>
        <button type="button" className="aur-profile-shortcut" onClick={() => navigate('/profile')}>
          <span className="aur-profile-avatar" aria-hidden="true">{profile?.avatar_url ? <img src={profile.avatar_url} alt="" /> : initials}</span>
          <span className="aur-navlabel">Einstellungen</span>
        </button>
        <button type="button" className="aur-side-logout" onClick={askForSignOut}
          title="Abmelden" aria-label="Abmelden" disabled={signingOut}>
          <LogOut className="aur-icon" aria-hidden="true" />
          <span className="aur-navlabel">{signingOut ? 'Wird abgemeldet…' : 'Abmelden'}</span>
        </button>
      </div>
    </>
  );

  const bottom = [
    { label: 'Start', Icon: Home, path: '/startseite' },
    { label: 'Training', Icon: Target, path: '/meine-trainings', also: ['/analyse'] },
    { label: 'Liga', Icon: Trophy, path: '/liga' },
    { label: 'Challenge', Icon: Flame, path: '/wochenchallenge', locked: 'challenge' as const },
  ];

  return (
    <>
      <aside className={`aur-sidebar ${tabletExpanded ? 'aur-sidebar-open' : ''}`} aria-label="AimUp Hauptnavigation">
        {desktopAndTabletNav}
      </aside>
      <button
        className={`aur-tablet-toggle ${tabletExpanded ? 'aur-tablet-toggle-open' : ''}`}
        onClick={() => setTabletExpanded(v => !v)}
        aria-label={tabletExpanded ? 'Navigation einklappen' : 'Navigation ausklappen'}
        aria-expanded={tabletExpanded}
        type="button"
      >
        {tabletExpanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>
      {tabletExpanded && <button className="aur-tablet-scrim" type="button" onClick={() => setTabletExpanded(false)} aria-label="Navigation schließen" />}
      <header className="aur-mobile-head">
        <button type="button" onClick={() => navigate('/startseite')} aria-label="Dashboard öffnen">
          <img src={logo} alt="AimUp" />
        </button>
        <button type="button" className="aur-mobile-avatar" onClick={() => navigate('/profile')} aria-label="Einstellungen">
          {profile?.avatar_url ? <img src={profile.avatar_url} alt="" /> : initials}
        </button>
      </header>
      <nav className="aur-bottom-nav" aria-label="Mobile Navigation">
        {bottom.map(item => (
          <button
            type="button"
            key={item.label}
            className={isActive(item.path, item.also) ? 'aur-bottom-active' : ''}
            onClick={() => go({ ...item, label: item.label })}
          >
            <item.Icon size={19} aria-hidden="true" />
            <span>{item.label}</span>
          </button>
        ))}
        <button className={mobileMore ? 'aur-bottom-active' : ''} type="button" onClick={() => setMobileMore(v => !v)} aria-expanded={mobileMore}>
          {mobileMore ? <X size={19} /> : <Menu size={19} />}
          <span>Mehr</span>
        </button>
      </nav>
      {mobileMore && (
        <>
          <button className="aur-drawer-scrim" type="button" onClick={() => setMobileMore(false)} aria-label="Menü schließen" tabIndex={-1} />
          <div className="aur-mobile-drawer" role="dialog" aria-label="Menü">
            <div className="aur-drawer-head">
              <h3>Menü</h3>
              <button type="button" className="aur-drawer-close" onClick={() => setMobileMore(false)} aria-label="Menü schließen">✕</button>
            </div>
            {sections.filter(section => section.title).map(section => (
              <Fragment key={section.title}>
                <div className="aur-drawer-section">{section.title}</div>
                {section.entries.map(entry => renderItem(entry, 'mobile'))}
              </Fragment>
            ))}
            <div className="aur-drawer-section">Weitere</div>
            <button type="button" className="aur-navitem" onClick={openFeedback}>
              <MessageSquare className="aur-icon" aria-hidden="true" />
              <span className="aur-navlabel">Feedback</span>
            </button>
            {more.map(entry => renderItem(entry, 'mobile'))}
            <button type="button" className="aur-navitem aur-drawer-signout" onClick={askForSignOut}
              aria-label="Abmelden" disabled={signingOut}>
              <LogOut className="aur-icon" aria-hidden="true" />
              <span className="aur-navlabel">{signingOut ? 'Wird abgemeldet…' : 'Abmelden'}</span>
            </button>
          </div>
        </>
      )}
      {askSignOut && (
        <AbmeldenDialog onCancel={() => setAskSignOut(false)} onConfirm={() => { setAskSignOut(false); void handleSignOut(); }} />
      )}
      {hint && <div className="aur-challenge-hint" role="status">{hint}</div>}
      {notice && (
        <div className="aur-notice" role="status">
          <LockKeyhole size={18} aria-hidden="true" />
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Hinweis schließen"><X size={17} /></button>
        </div>
      )}
    </>
  );
}
